import type { ReactNode } from 'react';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Box, IconButton, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { ErrorState } from '../ErrorState';
import { PageContainer } from '../PageContainer';

export interface SectionSearchConfig {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Disable the field (e.g. while the data source is not yet connected). */
  disabled?: boolean;
  /** Helper text shown under a disabled field to explain why. */
  disabledHint?: string;
}

export interface SectionScaffoldProps {
  /** Section title (rendered as the page `h1`). */
  title: ReactNode;
  /** Supporting subtitle. */
  subtitle?: ReactNode;
  /** Search box. Omit to hide the toolbar search. */
  search?: SectionSearchConfig;
  /** Optional primary action (e.g. an "Add" button). */
  primaryAction?: ReactNode;
  /** Destination for the gear button — the domain's settings screen. */
  settingsTo: string;
  /** Accessible label / tooltip for the gear (e.g. "Catalog settings"). */
  settingsLabel?: string;
  /** Whether the current employee may view this section. */
  canView?: boolean;
  children: ReactNode;
}

/**
 * Shared shell for a top-level operational section (Products, Orders, Customers,
 * Media, …). Owns the cross-cutting layout so each section only describes its
 * body: a title/subtitle header, an optional toolbar search, an optional primary
 * action, and a **settings gear** that deep-links to the domain's configuration.
 *
 * Operational data (the list/table) and its settings live side by side: the
 * section is where you *work*, the gear is where you *configure* — the standard
 * enterprise e-commerce pattern.
 */
export function SectionScaffold({
  title,
  subtitle,
  search,
  primaryAction,
  settingsTo,
  settingsLabel = 'Section settings',
  canView = true,
  children,
}: SectionScaffoldProps) {
  return (
    <PageContainer
      title={title}
      subtitle={subtitle}
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          {primaryAction}
          <Tooltip title={settingsLabel}>
            <IconButton
              component={RouterLink}
              to={settingsTo}
              aria-label={settingsLabel}
              sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}
            >
              <SettingsRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      {canView ? (
        <Stack spacing={2.5} sx={{ minHeight: '100%', flex: 1, alignSelf: 'stretch' }}>
          {search ? (
            <TextField
              value={search.value}
              onChange={(event) => search.onChange(event.target.value)}
              placeholder={search.placeholder ?? 'Search…'}
              disabled={search.disabled}
              helperText={search.disabled ? search.disabledHint : undefined}
              size="small"
              aria-label={search.placeholder ?? 'Search'}
              sx={{ maxWidth: { sm: 420 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {children}
          </Box>
        </Stack>
      ) : (
        <ErrorState
          title="Access restricted"
          message="You don't have permission to view this section. Contact a tenant administrator if you need access."
        />
      )}
    </PageContainer>
  );
}
